import React, { CSSProperties, useCallback, useEffect, useState, useRef, useMemo, memo } from 'react';

import styles from './CountHiddenTags.module.scss';
import { COUNT_TAGS_MORE_WIDTH, COUNT_TAGS_ROW_GAP, ITEM_HEIGHT, RECALC_DELAY } from './consts';
import { useThrottledSize } from './hooks';
import { ICountHiddenTagsProps } from './models';
import { MoreExternal } from './_moreExternal/MoreExternal';
import { Tag } from '@src/atoms/Tag';
import { Text } from '@src/atoms/Text';
import { TooltipNew } from '@src/atoms/TooltipNew';
import { classnames } from '@src/utils/classnames';

/** Мемоизация компонента позволяет ускорить в 2 раза перерисовку при ресайзах */
export const CountHiddenTags = memo(
  ({
    list,
    showRows = 2,
    itemHeight = ITEM_HEIGHT,
    id,
    size,
    delay = RECALC_DELAY,
    moreComponent,
    withMoreTooltip,
    tooltipContent,
    onHiddenChange,
    groupName,
    nameKey,
    viewKey,
    enableTagView = false,
    tagProps,
    onTagClick,
    expandable = false,
  }: ICountHiddenTagsProps) => {
    const wrapperHeight = itemHeight * showRows + (showRows - 1) * COUNT_TAGS_ROW_GAP;

    const wrapperRef = useRef<HTMLDivElement | null>(null);
    const [count, setCount] = useState(0);
    const [isExpanded, setIsExpanded] = useState(false);
    const { width: wrapperWidth } = useThrottledSize({ delay, ref: wrapperRef });

    /** Индекс с которого идет отсчет количества скрытых элементов. Если есть заголовок группы то его пропускаем */
    const startIndex = groupName ? 1 : 0;

    const fistHiddenIndex = count > 0 ? list.length - count + startIndex : -1;

    /** Нужно кешировать результаты, чтобы уменьшить пересчеты при одинаковых позициях скрытых элементов */
    const { countTop, countLeft, countOpacity } = useMemo(() => {
      const wrapperNode = wrapperRef.current;
      const fistVisibleIndex = fistHiddenIndex - 1;

      if (!wrapperNode || fistHiddenIndex === -1) return { countTop: 0, countLeft: 0, countOpacity: 0 };

      const firstVisibleNode = wrapperNode.children[fistVisibleIndex] as HTMLDivElement;
      const firstHidden = wrapperNode.children[fistHiddenIndex] as HTMLDivElement;

      const wrapperRect = wrapperNode?.getBoundingClientRect();
      const nodeRect = firstVisibleNode?.getBoundingClientRect();

      const wrapperRight = wrapperRect?.right;
      const nodeRight = nodeRect?.right;

      if (wrapperRight - nodeRight - COUNT_TAGS_ROW_GAP / 2 < COUNT_TAGS_MORE_WIDTH) {
        return {
          countTop: firstHidden?.offsetTop,
          countLeft: firstHidden?.offsetLeft,
          countOpacity: 1,
        };
      }

      return {
        countTop: firstVisibleNode?.offsetTop,
        countLeft: firstVisibleNode?.offsetLeft + firstVisibleNode?.clientWidth + COUNT_TAGS_ROW_GAP - 2,
        countOpacity: 1,
      };
    }, [fistHiddenIndex]);

    const handleResize = useCallback(() => {
      const wrapperNode = wrapperRef.current;

      if (!wrapperNode) return;

      let hidden = 0;
      let visible = 0;

      for (let i = startIndex; i <= wrapperNode.children.length; i++) {
        const checkItem = wrapperNode.children[i] as HTMLElement;

        if (!checkItem) continue;

        const topOffset = checkItem.offsetTop + itemHeight;

        if (topOffset > wrapperHeight) {
          hidden += 1;
        } else {
          visible += 1;
        }
      }

      if (hidden > 0) {
        const lastVisibleIndex = startIndex === 0 ? visible - 1 : visible;
        const lastVisible = wrapperNode.children[lastVisibleIndex];

        const { right: lastVisibleRight } = lastVisible.getBoundingClientRect();
        const { right: wrapperRight } = wrapperNode.getBoundingClientRect();

        if (wrapperRight - lastVisibleRight - COUNT_TAGS_ROW_GAP < COUNT_TAGS_MORE_WIDTH) {
          hidden += 1;
        }
      }

      setCount(hidden);
    }, [startIndex, itemHeight, wrapperHeight]);

    const moreStyles: CSSProperties = {
      width: COUNT_TAGS_MORE_WIDTH,
      top: countTop,
      left: countLeft,
      opacity: countOpacity,
      height: itemHeight,
    };

    useEffect(() => {
      handleResize();
    }, [wrapperWidth, handleResize]);

    useEffect(() => {
      if (onHiddenChange) {
        onHiddenChange(count, list.slice(fistHiddenIndex));
      }
    }, [count, onHiddenChange, list, fistHiddenIndex]);

    const toggleExpand = () => {
      setIsExpanded((prev) => !prev);
    };

    return (
      <div className={styles.root}>
        {count > 0 && !!moreComponent && (
          <MoreExternal
            moreComponent={moreComponent}
            moreStyles={moreStyles}
            withMoreTooltip={withMoreTooltip}
            tooltipContent={tooltipContent}
          />
        )}
        {!isExpanded && count > 0 && !moreComponent && (
          <Tag
            id={`countTagMore_${id}`}
            style={{ ...moreStyles, height: size ? 'unset' : itemHeight }}
            className={`${styles.more} ${styles.tag}`}
            size={size}
            onClick={expandable ? toggleExpand : undefined}
            clickable={expandable}
          >
            Ещё {count}
          </Tag>
        )}
        <div className={styles.wrapper} style={{ maxHeight: isExpanded ? 'none' : wrapperHeight }} ref={wrapperRef}>
          {groupName && (
            <TooltipNew content={groupName}>
              <Text
                id={`groupName_${id}`}
                size="secondary"
                color="basicBlack"
                className={styles.groupName}
                noOfLines={1}
              >
                {groupName}:
              </Text>
            </TooltipNew>
          )}
          {list.map((item, index) => {
            /** Увеличиваем текущий индекс, потому что отсчет тегов идет после названия */
            const checkIndex = groupName ? index + 1 : index;

            return (
              <Tag
                id={`countTag_${item.id}`}
                style={{
                  height: size ? size : itemHeight,
                  opacity: !isExpanded && fistHiddenIndex > 0 && checkIndex >= fistHiddenIndex ? '0' : '1',
                }}
                key={item.id}
                size={size}
                className={classnames(!enableTagView && styles.tag)}
                view={enableTagView && viewKey ? item[viewKey] : undefined}
                onClick={() => onTagClick?.(item)}
                clickable={item.clickable}
                textColor={item.textColor}
                {...tagProps}
              >
                {item[nameKey]}
              </Tag>
            );
          })}
          {isExpanded && (
            <Tag
              id={`hideTags_${id}`}
              style={{ ...moreStyles, width: 80, height: size ? 'unset' : itemHeight }}
              size={size}
              onClick={toggleExpand}
              clickable
            >
              Скрыть
            </Tag>
          )}
        </div>
      </div>
    );
  },
);

CountHiddenTags.displayName = 'CountHiddenTags';
